/**
 * AI Context Generation Service
 * 
 * This service creates comprehensive historical context for AI insights generation.
 * When analyzing SIMCAT 6, it provides context from SIMCAT 1-5 to enable meaningful
 * comparative analysis and trend identification.
 * 
 * Key Features:
 * - Historical performance trends
 * - Section-wise progression analysis  
 * - Cutoff clearing patterns
 * - Strength/weakness evolution
 * - Learning curve insights
 * - Question selection pattern changes
 * - Test attempt date analysis and time gap insights
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Import test attempt date service for date-based analysis
import testAttemptDateService from './testAttemptDateService';
import StudentIdUtils from './studentIdUtils';

class AIContextService {
  constructor() {
    this.contextCache = new Map();
    this.maxContextTests = 5; // Maximum previous tests to include in context
  }

  /**
   * Generate comprehensive AI context for a specific test
   * @param {string} currentTestId - The test being analyzed  
   * @param {string} studentId - Student IMS PIN
   * @param {Array} availableTests - All available tests for the student
   * @returns {Object} Rich context object for AI prompts
   */
  async generateAIContext(currentTestId, studentId, availableTests) {
    console.log(`🧠 AIContext: Generating context for test ${currentTestId}, student ${studentId}`);
    
    // Get historical tests using actual end dates from MongoDB
    const historicalTests = await this.getHistoricalTestsForContext(currentTestId, studentId);
    
    if (historicalTests.length === 0) {
      return this.generateFirstTestContext(currentTestId, studentId);
    }

    // Fetch analytics data for all historical tests
    const historicalData = await this.fetchHistoricalAnalytics(studentId, historicalTests);
    
    // Fetch attempt dates for date-based analysis
    const attemptDates = await testAttemptDateService.getStudentTestAttemptDates(studentId);
    
    // Add attempt dates to historical data
    const enrichedHistoricalData = this.enrichWithAttemptDates(historicalData, attemptDates);
    
    // Generate date-based context
    const dateContext = await this.generateDateBasedContext(currentTestId, studentId, availableTests, attemptDates);
    
    // Generate comprehensive context
    const context = {
      currentTest: {
        id: currentTestId,
        position: this.getTestPosition(currentTestId, availableTests),
        isFirst: historicalTests.length === 0,
        attemptDate: this.getTestAttemptDate(currentTestId, attemptDates)
      },
      historical: {
        testCount: historicalTests.length,
        tests: enrichedHistoricalData,
        trends: this.analyzeTrends(enrichedHistoricalData),
        patterns: this.identifyPatterns(enrichedHistoricalData),
        progression: this.analyzeProgression(enrichedHistoricalData)
      },
      dateAnalysis: dateContext,
      contextSummary: this.generateContextSummary(enrichedHistoricalData, dateContext),
      promptContext: this.generatePromptContext(currentTestId, enrichedHistoricalData, dateContext)
    };

    console.log(`📊 AIContext: Generated context with ${historicalTests.length} historical tests and date analysis`);
    return context;
  }

  /**
   * Get historical tests for AI context
   * DEPRECATED: MongoDB endpoint removed - historical data now comes from API via backend
   * @param {string} currentTestId - The test being analyzed
   * @param {string} studentId - Student IMS PIN
   * @returns {Array} Empty array (historical data now processed on backend)
   */
  async getHistoricalTestsForContext(currentTestId, studentId) {
    console.log(`📅 AIContext: DEPRECATED - Historical tests now fetched by backend via API`);
    return [];
  }

  /**
   * Get SIMCAT number from test ID or test name
   */
  getSimcatNumber(testId, availableTests) {
    const test = availableTests.find(t => t.testId === testId);
    if (!test) return 0;
    
    const match = test.testName.match(/SIMCAT\s*(\d+)/i);
    return match ? parseInt(match[1]) : 0;
  }

  /**
   * Get test position in sequence (SIMCAT number) - wrapper for backward compatibility
   */
  getTestPosition(testId, availableTests) {
    return this.getSimcatNumber(testId, availableTests);
  }

  /**
   * Fetch analytics data for historical tests
   */
  async fetchHistoricalAnalytics(studentId, historicalTests) {
    const historicalData = [];
    
    for (const test of historicalTests) {
      try {
        console.log(`📡 AIContext: Fetching data for ${test.testName}`);
        
        // Ensure clean student PIN for API calls
        const cleanStudentPin = StudentIdUtils.extractCleanPin(studentId);
        const response = await fetch(
          `${API_BASE_URL}/student/student_${cleanStudentPin}/test/${test.testId}/analytics`
        );
        
        if (response.ok) {
          const analyticsData = await response.json();
          
          // Process and summarize the analytics data
          const processedData = this.processAnalyticsData(analyticsData, test);
          historicalData.push(processedData);
          
          console.log(`✅ AIContext: Processed data for ${test.testName}`);
        } else {
          console.warn(`⚠️ AIContext: No analytics data for ${test.testName} (${response.status})`);
        }
      } catch (error) {
        console.warn(`⚠️ AIContext: Failed to fetch data for ${test.testName}:`, error);
      }
    }
    
    return historicalData;
  }

  /**
   * Process raw analytics data into context-friendly format
   */
  processAnalyticsData(analyticsResponse, testInfo) {
    const { data, percentileData, metadata } = analyticsResponse;
    
    // Calculate key metrics
    const sections = ["QA", "VARC", "DILR"];
    const studentColumn = `student_${metadata.studentPin}`;
    
    const sectionMetrics = {};
    const overallMetrics = {
      attempted: 0,
      correct: 0,
      total: data.length
    };

    // Calculate section-wise metrics
    sections.forEach(section => {
      const sectionQuestions = data.filter(q => q.section === section);
      const attempted = sectionQuestions.filter(q => 
        q[studentColumn] && q[studentColumn].toLowerCase() !== 'skipped'
      );
      const correct = attempted.filter(q => q[studentColumn].toLowerCase() === 'correct');
      
      sectionMetrics[section] = {
        total: sectionQuestions.length,
        attempted: attempted.length,
        correct: correct.length,
        accuracy: attempted.length > 0 ? (correct.length / attempted.length * 100) : 0,
        attemptRate: (attempted.length / sectionQuestions.length * 100),
        percentile: percentileData?.[`${section.toLowerCase()}_percentiles`] || 0
      };
      
      overallMetrics.attempted += attempted.length;
      overallMetrics.correct += correct.length;
    });

    overallMetrics.accuracy = overallMetrics.attempted > 0 ? 
      (overallMetrics.correct / overallMetrics.attempted * 100) : 0;
    overallMetrics.attemptRate = (overallMetrics.attempted / overallMetrics.total * 100);
    overallMetrics.percentile = percentileData?.overall_percentiles || 0;

    return {
      test: {
        id: testInfo.testId,
        name: testInfo.testName,
        position: this.getSimcatNumber(testInfo.testId, [testInfo])
      },
      sections: sectionMetrics,
      overall: overallMetrics,
      cutoffs: this.calculateCutoffStatus(sectionMetrics, overallMetrics),
      questionSelection: this.analyzeQuestionSelection(data, studentColumn)
    };
  }

  /**
   * Calculate cutoff clearing status
   *
   * Why no hardcoded CUTOFFS? Project rule: "No fallback percentile cutoffs in production".
   * This method previously hardcoded { QA: 20, VARC: 25, DILR: 20, OVERALL: 80 } — the
   * exact values explicitly prohibited by project rules. Real cutoffs must come from
   * backend/MongoDB via the `categoryCutoffs` prop path in score-card.js. Since this
   * legacy client-side helper cannot access that source, it now returns UNKNOWN for all
   * sections — downstream consumers should rely on the backend-computed cutoff_status.
   */
  calculateCutoffStatus(sectionMetrics, overallMetrics) {
    const sections = ['QA', 'VARC', 'DILR', 'OVERALL'];
    const cutoffStatus = {};

    sections.forEach(section => {
      const estimatedScore = section === 'OVERALL'
        ? Math.round(overallMetrics.correct * 3 - (overallMetrics.attempted - overallMetrics.correct) * 0.5)
        : (sectionMetrics[section]
            ? Math.round(sectionMetrics[section].correct * 3 - (sectionMetrics[section].attempted - sectionMetrics[section].correct) * 0.5)
            : null);

      cutoffStatus[section] = {
        cleared: null, // cannot determine without real cutoff from backend
        score: estimatedScore,
        status: 'UNKNOWN'
      };
    });

    return cutoffStatus;
  }

  /**
   * Analyze question selection patterns
   */
  analyzeQuestionSelection(data, studentColumn) {
    const difficultyStats = { A: { total: 0, attempted: 0 }, B: { total: 0, attempted: 0 }, C: { total: 0, attempted: 0 } };
    
    data.forEach(question => {
      const difficulty = question.difficulty_level || 'B'; // Default to B if not specified
      if (difficultyStats[difficulty]) {
        difficultyStats[difficulty].total++;
        
        if (question[studentColumn] && question[studentColumn].toLowerCase() !== 'skipped') {
          difficultyStats[difficulty].attempted++;
        }
      }
    });

    // Calculate attempt percentages
    Object.keys(difficultyStats).forEach(level => {
      const stats = difficultyStats[level];
      stats.attemptPercentage = stats.total > 0 ? (stats.attempted / stats.total * 100) : 0;
    });

    return difficultyStats;
  }

  /**
   * Analyze performance trends across tests
   */
  analyzeTrends(historicalData) {
    if (historicalData.length < 2) return { status: 'insufficient_data' };
    
    const trends = {
      overall: this.calculateTrend(historicalData.map(d => d.overall.percentile)),
      sections: {},
      accuracy: this.calculateTrend(historicalData.map(d => d.overall.accuracy)),
      attemptRate: this.calculateTrend(historicalData.map(d => d.overall.attemptRate))
    };

    // Section-wise trends
    ["QA", "VARC", "DILR"].forEach(section => {
      trends.sections[section] = {
        percentile: this.calculateTrend(historicalData.map(d => d.sections[section].percentile)),
        accuracy: this.calculateTrend(historicalData.map(d => d.sections[section].accuracy))
      };
    });

    return trends;
  }

  /**
   * Calculate trend direction (improving/declining/stable)
   */
  calculateTrend(values) {
    if (values.length < 2) return 'stable';
    
    const firstHalf = values.slice(0, Math.ceil(values.length / 2));
    const secondHalf = values.slice(Math.floor(values.length / 2));
    
    const firstAvg = firstHalf.reduce((a, b) => a + b, 0) / firstHalf.length;
    const secondAvg = secondHalf.reduce((a, b) => a + b, 0) / secondHalf.length;
    
    const change = secondAvg - firstAvg;
    
    if (change > 5) return 'improving';
    if (change < -5) return 'declining';
    return 'stable';
  }

  /**
   * Identify performance patterns
   */
  identifyPatterns(historicalData) {
    const patterns = {
      strongestSection: this.findStrongestSection(historicalData),
      weakestSection: this.findWeakestSection(historicalData),
      mostImproved: this.findMostImprovedSection(historicalData),
      consistentCutoffs: this.findConsistentCutoffs(historicalData),
      questionSelectionEvolution: this.analyzeQuestionSelectionEvolution(historicalData)
    };

    return patterns;
  }

  /**
   * Find consistently strongest section
   */
  findStrongestSection(historicalData) {
    const sectionAverages = { QA: 0, VARC: 0, DILR: 0 };
    
    historicalData.forEach(test => {
      Object.keys(sectionAverages).forEach(section => {
        sectionAverages[section] += test.sections[section].percentile;
      });
    });

    Object.keys(sectionAverages).forEach(section => {
      sectionAverages[section] /= historicalData.length;
    });

    return Object.entries(sectionAverages)
      .sort(([,a], [,b]) => b - a)[0][0];
  }

  /**
   * Find consistently weakest section  
   */
  findWeakestSection(historicalData) {
    const sectionAverages = { QA: 0, VARC: 0, DILR: 0 };
    
    historicalData.forEach(test => {
      Object.keys(sectionAverages).forEach(section => {
        sectionAverages[section] += test.sections[section].percentile;
      });
    });

    Object.keys(sectionAverages).forEach(section => {
      sectionAverages[section] /= historicalData.length;
    });

    return Object.entries(sectionAverages)
      .sort(([,a], [,b]) => a - b)[0][0];
  }

  /**
   * Find most improved section
   */
  findMostImprovedSection(historicalData) {
    if (historicalData.length < 2) return null;
    
    const improvements = {};
    ["QA", "VARC", "DILR"].forEach(section => {
      const first = historicalData[0].sections[section].percentile;
      const last = historicalData[historicalData.length - 1].sections[section].percentile;
      improvements[section] = last - first;
    });

    return Object.entries(improvements)
      .sort(([,a], [,b]) => b - a)[0][0];
  }

  /**
   * Find sections with consistent cutoff clearing
   */
  findConsistentCutoffs(historicalData) {
    const cutoffConsistency = { QA: 0, VARC: 0, DILR: 0, OVERALL: 0 };
    
    historicalData.forEach(test => {
      Object.keys(cutoffConsistency).forEach(section => {
        if (test.cutoffs[section].cleared) {
          cutoffConsistency[section]++;
        }
      });
    });

    // Convert to percentages
    Object.keys(cutoffConsistency).forEach(section => {
      cutoffConsistency[section] = (cutoffConsistency[section] / historicalData.length * 100);
    });

    return cutoffConsistency;
  }

  /**
   * Analyze question selection evolution
   */
  analyzeQuestionSelectionEvolution(historicalData) {
    if (historicalData.length < 2) return null;
    
    const evolution = { A: [], B: [], C: [] };
    
    historicalData.forEach(test => {
      Object.keys(evolution).forEach(level => {
        evolution[level].push(test.questionSelection[level].attemptPercentage);
      });
    });

    return {
      A: this.calculateTrend(evolution.A),
      B: this.calculateTrend(evolution.B), 
      C: this.calculateTrend(evolution.C)
    };
  }

  /**
   * Analyze learning progression
   */
  analyzeProgression(historicalData) {
    return {
      overallGrowth: this.calculateGrowthRate(historicalData.map(d => d.overall.percentile)),
      consistencyScore: this.calculateConsistency(historicalData.map(d => d.overall.percentile)),
      peakPerformance: this.findPeakPerformance(historicalData),
      improvementAreas: this.identifyImprovementAreas(historicalData)
    };
  }

  /**
   * Calculate growth rate
   */
  calculateGrowthRate(values) {
    if (values.length < 2) return 0;
    return ((values[values.length - 1] - values[0]) / values.length);
  }

  /**
   * Calculate consistency score (lower variance = higher consistency)
   */
  calculateConsistency(values) {
    if (values.length < 2) return 100;
    
    const mean = values.reduce((a, b) => a + b) / values.length;
    const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
    
    // Convert to consistency score (0-100, higher is more consistent)
    return Math.max(0, 100 - variance);
  }

  /**
   * Find peak performance test
   */
  findPeakPerformance(historicalData) {
    if (historicalData.length === 0) return null;
    
    return historicalData.reduce((peak, current) => {
      return current.overall.percentile > peak.overall.percentile ? current : peak;
    });
  }

  /**
   * Identify areas needing improvement
   */
  identifyImprovementAreas(historicalData) {
    const areas = [];
    
    // Check sections with declining trends
    const trends = this.analyzeTrends(historicalData);
    Object.entries(trends.sections).forEach(([section, trend]) => {
      if (trend.percentile === 'declining') {
        areas.push({
          area: section,
          issue: 'declining_percentile',
          severity: 'high'
        });
      }
    });

    // Check sections with low cutoff clearing rate
    const cutoffs = this.findConsistentCutoffs(historicalData);
    Object.entries(cutoffs).forEach(([section, rate]) => {
      if (rate < 50) {
        areas.push({
          area: section,
          issue: 'low_cutoff_rate',
          severity: rate < 25 ? 'high' : 'medium'
        });
      }
    });

    return areas;
  }

  /**
   * Generate context summary for AI consumption
   */
  generateContextSummary(historicalData) {
    if (historicalData.length === 0) {
      return "This is the student's first SIMCAT. No historical context available.";
    }

    const trends = this.analyzeTrends(historicalData);
    const patterns = this.identifyPatterns(historicalData);
    
    let summary = `Historical Context (${historicalData.length} previous tests):\\n`;
    summary += `- Overall trend: ${trends.overall}\\n`;
    summary += `- Strongest section: ${patterns.strongestSection}\\n`;
    summary += `- Weakest section: ${patterns.weakestSection}\\n`;
    summary += `- Most improved: ${patterns.mostImproved || 'None'}\\n`;
    
    return summary;
  }

  /**
   * Generate prompt-ready context
   */
  generatePromptContext(currentTestId, historicalData) {
    if (historicalData.length === 0) {
      return {
        prefix: "FIRST TEST ANALYSIS",
        context: "This is the student's first recorded SIMCAT. Focus on baseline establishment and foundational insights.",
        historical: ""
      };
    }

    const testPosition = historicalData.length + 1;
    const trends = this.analyzeTrends(historicalData);
    const patterns = this.identifyPatterns(historicalData);

    let contextText = `HISTORICAL PERFORMANCE CONTEXT (Tests 1-${historicalData.length}):\\n\\n`;
    
    // Performance evolution
    contextText += `PERFORMANCE EVOLUTION:\\n`;
    contextText += `- Overall trend: ${trends.overall}\\n`;
    contextText += `- Accuracy trend: ${trends.accuracy}\\n`;
    contextText += `- Attempt rate trend: ${trends.attemptRate}\\n\\n`;

    // Section analysis
    contextText += `SECTION ANALYSIS:\\n`;
    contextText += `- Consistently strongest: ${patterns.strongestSection}\\n`;
    contextText += `- Needs improvement: ${patterns.weakestSection}\\n`;
    contextText += `- Most improved: ${patterns.mostImproved || 'None significant'}\\n\\n`;

    // Cutoff patterns
    contextText += `CUTOFF CLEARING PATTERN:\\n`;
    Object.entries(patterns.consistentCutoffs).forEach(([section, rate]) => {
      contextText += `- ${section}: ${rate.toFixed(0)}% success rate\\n`;
    });

    // Latest performance for comparison
    const latestTest = historicalData[historicalData.length - 1];
    contextText += `\\nLATEST PERFORMANCE (${latestTest.test.name}):\\n`;
    contextText += `- Overall percentile: ${latestTest.overall.percentile.toFixed(1)}\\n`;
    contextText += `- Overall accuracy: ${latestTest.overall.accuracy.toFixed(1)}%\\n`;
    Object.entries(latestTest.sections).forEach(([section, data]) => {
      contextText += `- ${section}: ${data.percentile.toFixed(1)}%ile, ${data.accuracy.toFixed(1)}% accuracy\\n`;
    });

    return {
      prefix: `TEST ${testPosition} ANALYSIS WITH HISTORICAL CONTEXT`,
      context: `Analyzing test ${testPosition} with context from ${historicalData.length} previous tests.`,
      historical: contextText
    };
  }

  /**
   * Generate first test context with attempt date
   */
  generateFirstTestContext(currentTestId, studentId) {
    return {
      currentTest: {
        id: currentTestId,
        position: 1,
        isFirst: true,
        attemptDate: null
      },
      historical: {
        testCount: 0,
        tests: [],
        trends: { status: 'first_test' },
        patterns: { status: 'first_test' },
        progression: { status: 'first_test' }
      },
      dateAnalysis: {
        timeContext: 'first_test',
        gapAnalysis: null,
        preparation: 'initial'
      },
      contextSummary: "This is the student's first SIMCAT. No historical context available.",
      promptContext: {
        prefix: "FIRST TEST ANALYSIS",
        context: "This is the student's first recorded SIMCAT. Focus on baseline establishment and foundational insights.",
        historical: "",
        dateContext: "First attempt - establishing baseline performance."
      }
    };
  }

  /**
   * Enrich historical data with attempt dates
   */
  enrichWithAttemptDates(historicalData, attemptDates) {
    return historicalData.map(testData => {
      const attemptInfo = attemptDates.find(date => date.testId === testData.test.id);
      return {
        ...testData,
        attemptDate: attemptInfo?.attemptDate || null,
        formattedDate: attemptInfo?.formattedDate || 'Unknown date'
      };
    });
  }

  /**
   * Get attempt date for a specific test
   */
  getTestAttemptDate(testId, attemptDates) {
    const attemptInfo = attemptDates.find(date => date.testId === testId);
    return attemptInfo?.formattedDate || null;
  }

  /**
   * Generate date-based context analysis
   */
  async generateDateBasedContext(currentTestId, studentId, availableTests, attemptDates) {
    try {
      // Analyze attempt pattern
      const patternAnalysis = await testAttemptDateService.analyzeAttemptPattern(studentId, availableTests);
      
      // Get date-based AI context
      const testsWithDates = attemptDates.map(dateInfo => ({
        testId: dateInfo.testId,
        attemptDate: dateInfo.attemptDate
      }));
      
      const dateBasedContext = testAttemptDateService.generateDateBasedAIContext(currentTestId, testsWithDates);
      
      return {
        pattern: patternAnalysis,
        context: dateBasedContext,
        currentTestDate: this.getTestAttemptDate(currentTestId, attemptDates)
      };
    } catch (error) {
      console.error('⚠️ AIContext: Error generating date-based context:', error);
      return {
        pattern: { totalTests: 0, pattern: 'unknown' },
        context: { timeContext: 'unknown', preparation: 'unknown' },
        currentTestDate: null
      };
    }
  }

  /**
   * Clear context cache
   */
  clearCache() {
    this.contextCache.clear();
  }

  /**
   * Generate context summary for AI consumption (enhanced with date analysis)
   */
  generateContextSummary(historicalData, dateContext) {
    if (historicalData.length === 0) {
      return "This is the student's first SIMCAT. No historical context available.";
    }

    const trends = this.analyzeTrends(historicalData);
    const patterns = this.identifyPatterns(historicalData);
    
    let summary = `Historical Context (${historicalData.length} previous tests):\n`;
    summary += `- Overall trend: ${trends.overall}\n`;
    summary += `- Strongest section: ${patterns.strongestSection}\n`;
    summary += `- Weakest section: ${patterns.weakestSection}\n`;
    summary += `- Most improved: ${patterns.mostImproved || 'None'}\n`;
    
    // Add date-based insights
    if (dateContext?.pattern) {
      summary += `- Testing pattern: ${dateContext.pattern.pattern} (${dateContext.pattern.consistency})\n`;
      if (dateContext.pattern.averageGap) {
        summary += `- Average gap between tests: ${dateContext.pattern.averageGap} days\n`;
      }
    }
    
    if (dateContext?.context?.gapAnalysis) {
      const gap = dateContext.context.gapAnalysis;
      summary += `- Time since last test: ${gap.daysBetween} days (${gap.isConsistent ? 'consistent' : 'unusual'} gap)\n`;
    }
    
    return summary;
  }

  /**
   * Generate prompt-ready context (enhanced with date analysis)
   */
  generatePromptContext(currentTestId, historicalData, dateContext) {
    if (historicalData.length === 0) {
      return {
        prefix: "FIRST TEST ANALYSIS",
        context: "This is the student's first recorded SIMCAT. Focus on baseline establishment and foundational insights.",
        historical: "",
        dateContext: "First attempt - establishing baseline performance."
      };
    }

    const testPosition = historicalData.length + 1;
    const trends = this.analyzeTrends(historicalData);
    const patterns = this.identifyPatterns(historicalData);

    let contextText = `HISTORICAL PERFORMANCE CONTEXT (Tests 1-${historicalData.length}):\n\n`;
    
    // Performance evolution
    contextText += `PERFORMANCE EVOLUTION:\n`;
    contextText += `- Overall trend: ${trends.overall}\n`;
    contextText += `- Accuracy trend: ${trends.accuracy}\n`;
    contextText += `- Attempt rate trend: ${trends.attemptRate}\n\n`;

    // Section analysis
    contextText += `SECTION ANALYSIS:\n`;
    contextText += `- Consistently strongest: ${patterns.strongestSection}\n`;
    contextText += `- Needs improvement: ${patterns.weakestSection}\n`;
    contextText += `- Most improved: ${patterns.mostImproved || 'None significant'}\n\n`;

    // Cutoff patterns
    contextText += `CUTOFF CLEARING PATTERN:\n`;
    Object.entries(patterns.consistentCutoffs).forEach(([section, rate]) => {
      contextText += `- ${section}: ${rate.toFixed(0)}% success rate\n`;
    });

    // Latest performance for comparison
    const latestTest = historicalData[historicalData.length - 1];
    contextText += `\nLATEST PERFORMANCE (${latestTest.test.name}):`;
    if (latestTest.attemptDate) {
      contextText += ` - Attempted on ${latestTest.formattedDate}`;
    }
    contextText += `\n- Overall percentile: ${latestTest.overall.percentile.toFixed(1)}\n`;
    contextText += `- Overall accuracy: ${latestTest.overall.accuracy.toFixed(1)}%\n`;
    Object.entries(latestTest.sections).forEach(([section, data]) => {
      contextText += `- ${section}: ${data.percentile.toFixed(1)}%ile, ${data.accuracy.toFixed(1)}% accuracy\n`;
    });

    // Add date-based context
    let dateContextText = "";
    if (dateContext?.context?.gapAnalysis) {
      const gap = dateContext.context.gapAnalysis;
      dateContextText += `\nTEST TIMING ANALYSIS:\n`;
      dateContextText += `- Days since last test: ${gap.daysBetween}\n`;
      dateContextText += `- Preparation time: ${dateContext.context.preparation} (${gap.isConsistent ? 'typical' : 'unusual'} gap)\n`;
      
      if (dateContext.context.preparation === 'rushed') {
        dateContextText += `- Note: Short preparation time may impact performance\n`;
      } else if (dateContext.context.preparation === 'extended') {
        dateContextText += `- Note: Extended gap may affect test-taking rhythm\n`;
      }
    }
    
    if (dateContext?.pattern?.pattern && dateContext.pattern.totalTests > 1) {
      dateContextText += `- Overall testing pattern: ${dateContext.pattern.pattern} (${dateContext.pattern.consistency})\n`;
      if (dateContext.pattern.averageGap) {
        dateContextText += `- Average preparation time: ${dateContext.pattern.averageGap} days\n`;
      }
    }

    return {
      prefix: `TEST ${testPosition} ANALYSIS WITH HISTORICAL CONTEXT`,
      context: `Analyzing test ${testPosition} with context from ${dateContext?.currentTestDate ? `${dateContext.currentTestDate} - ` : ''}${historicalData.length} previous tests.`,
      historical: contextText,
      dateContext: dateContextText
    };
  }
}

export default new AIContextService();